import { useEffect, useRef } from 'react'
import type { ClientOngoingLoad } from '@numeo/types-core'
import type { TmsLoadDocumentView } from '@numeo/types-main-web-app'
import { ongoingLoadsApi } from '@/pages/dashboard/pages/updater-agent/pages/ongoing-loads/api/ongoing-loads.api'

/** How often the load is read while its POD's check runs, and for how long a card waits before the user's "Check again" is left. */
export const POD_CHECK_POLL_MS = 8_000
export const POD_CHECK_WAIT_MS = 4 * 60_000

/** Whether any POD check on the load has read this file: the check names the documents it read. */
export function checkReadFile(load: Pick<ClientOngoingLoad, 'podCheck' | 'route'>, url: string): boolean {
    const checks = [load.podCheck, ...(load.route ?? []).map((stop) => stop.podCheck)]
    return checks.some((check) => (check as { sourceDocumentUrls?: string[] } | undefined)?.sourceDocumentUrls?.includes(url))
}

/**
 * While a POD the user just filed is still being checked, reads the load every few seconds and calls `onFinished` once when the
 * check has read the newest file, so the card shows what it found without the user asking. Only a card made just now watches
 * (`as_of` within the wait): a card read back from the chat's history never starts a turn by itself. Stops at the wait, on a
 * failed read, or when `enabled` turns false (the user asked themselves).
 */
export function usePodCheckWatch(document: TmsLoadDocumentView, enabled: boolean, onFinished: () => void): void {
    const finished = useRef(onFinished)
    finished.current = onFinished
    const newest = document.files[0]?.url
    const fresh = Date.now() - new Date(document.as_of).getTime() < POD_CHECK_WAIT_MS
    const watch = enabled && fresh && document.document === 'pod' && document.check.state === 'running' && !!newest

    useEffect(() => {
        if (!watch || !newest) return
        let stopped = false
        const started = Date.now()
        const timer = setInterval(() => {
            if (stopped) return
            if (Date.now() - started > POD_CHECK_WAIT_MS) {
                clearInterval(timer)
                return
            }
            ongoingLoadsApi
                .getOngoingLoadById(document.load_id)
                .then((load) => {
                    if (stopped || !checkReadFile(load, newest)) return
                    stopped = true
                    clearInterval(timer)
                    finished.current()
                })
                .catch(() => {
                    stopped = true
                    clearInterval(timer)
                })
        }, POD_CHECK_POLL_MS)
        return () => {
            stopped = true
            clearInterval(timer)
        }
    }, [watch, newest, document.load_id])
}
